import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../types';
import { useScheduleStore } from '../store';
import {
  signIn as authSignIn,
  signUp as authSignUp,
  signOut as authSignOut,
  resetPassword as authResetPassword,
  updatePassword as authUpdatePassword,
  migrateLocalSchedule,
  getCurrentSession,
  toAppUser
} from '../services/auth';
import { supabase } from '../services/supabase';
import { formatErrorMessage, ServiceError } from '../services/error';

interface AuthErrorResponse {
  message: string;
  type: string;
}

interface AuthContextType {
  user: User | null;
  signIn: (email: string, password: string) => Promise<{ error?: AuthErrorResponse }>;
  signUp: (email: string, password: string) => Promise<{ error?: AuthErrorResponse; requiresEmailConfirmation?: boolean }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Read schedule lazily so we always get the current value at call time
  const getSchedule = () => useScheduleStore.getState().schedule;

  useEffect(() => {
    // Check active sessions and sets the user
    getCurrentSession().then(session => {
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch(error => {
      console.error('Error getting session:', error);
      setLoading(false);
    });

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(toAppUser(session?.user ?? null));
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    try {
      const localSchedule = getSchedule();
      
      const response = await authSignUp({ email, password }, localSchedule || undefined);
      return { requiresEmailConfirmation: response.requiresEmailConfirmation };
    } catch (err) {
      const error = err as ServiceError;
      return { 
        error: { 
          message: formatErrorMessage(error), 
          type: error.code 
        } 
      };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const localSchedule = getSchedule();
      
      // First try to sign in
      const response = await authSignIn({ email, password });
      
      // If successful and there's a local schedule, try to migrate it
      if (response.user && localSchedule && localSchedule.timeBlocks.length > 0) {
        try {
          // Get existing saved schedules for this user
          const { data: existingSchedules } = await supabase
            .from('saved_schedules')
            .select('id')
            .eq('user_id', response.user.id);

          // Only migrate if the user doesn't have any saved schedules yet
          if (!existingSchedules || existingSchedules.length === 0) {
            await migrateLocalSchedule(localSchedule, response.user.id);
          }
        } catch (saveError) {
          console.error('Error migrating local schedule during sign-in:', saveError);
          // Don't fail the sign-in if migration fails
        }
      }

      return {};
    } catch (err) {
      const error = err as ServiceError;
      
      // Check if it's a network error
      if (error.code === 'network/connection-error') {
        return { 
          error: { 
            message: 'Unable to connect to the authentication service. Please check your internet connection and try again.', 
            type: 'network' 
          } 
        };
      }
      
      return { 
        error: { 
          message: formatErrorMessage(error), 
          type: error.code 
        } 
      };
    }
  };

  const signOut = async () => {
    try {
      await authSignOut();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await authResetPassword(email);
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      await authUpdatePassword(newPassword);
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, signIn, signUp, signOut, resetPassword, updatePassword, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// Auth hooks live next to the provider to preserve the existing public import path.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
